import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Copy, Download, Loader2, Stamp } from "lucide-react";
import { api } from "../lib/api";
import { IMAGES } from "../lib/tokens";
import { useWorld } from "../state/World";
import Plate from "../components/Plate";

export default function MissionPage() {
  const { mark } = useWorld();
  const nav = useNavigate();
  const [mission, setMission] = useState(null);
  const [marks, setMarks] = useState([]);
  const [exp, setExp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    mark("stageward", "/stageward/mission");
    api.get("/stageward/project").then(({ data }) => {
      setMission(data.mission);
      setMarks(data.marks || []);
      if (!data.mission) nav("/stageward/table");
    });
  }, [mark, nav]);

  const strikeMark = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stageward/mark", {
        label: `Mission set — ${mission.title}`,
        detail: mission.first_step,
        realm: "stageward",
        ref_id: mission.id,
      });
      setMarks((m) => [data, ...m]);
      toast.success("Mark struck. No Tokens moved — Marks are not currency.");
    } catch (e) {
      toast.error("The Mark did not take.");
    } finally {
      setBusy(false);
    }
  };

  const doExport = async () => {
    const { data } = await api.get("/stageward/export");
    setExp(data);
  };

  const download = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!mission) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-white/30" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Plate src={IMAGES.foyer} depth={9} />

      <div className="relative z-10 mx-auto max-w-5xl px-6 pb-28 pt-20 md:px-10">
        <button
          type="button"
          data-testid="back-to-table"
          onClick={() => nav("/stageward/table")}
          className="tracked mb-12 flex items-center gap-2 text-[9px] text-white/40 transition-colors duration-200 hover:text-[var(--stage-gold)]"
        >
          <ArrowLeft size={12} /> The Table
        </button>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="glass-warm rounded-2xl p-8 md:p-12"
          data-testid="mission-panel"
        >
          <div className="tracked mb-7 text-[10px] text-[var(--stage-gold)]/75">The Mission</div>
          <h1
            className="serif mb-7 text-4xl font-light leading-[1.08] text-[#f7ecd8] sm:text-5xl"
            data-testid="mission-title"
          >
            {mission.title}
          </h1>
          <p className="mb-10 max-w-2xl text-sm leading-relaxed text-white/55 md:text-base">
            {mission.why}
          </p>
          <div className="grid gap-8 border-t border-white/8 pt-8 sm:grid-cols-2">
            <div>
              <div className="mono mb-2 text-[9px] text-white/30">First step</div>
              <div className="serif text-lg leading-snug text-[#f6ead4]" data-testid="mission-first-step">
                {mission.first_step}
              </div>
            </div>
            <div>
              <div className="mono mb-2 text-[9px] text-white/30">Horizon</div>
              <div className="serif text-lg leading-snug text-[#f6ead4]">{mission.horizon}</div>
            </div>
          </div>
        </motion.div>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="glass rounded-2xl p-7"
            data-testid="mark-panel"
          >
            <div className="tracked mb-3 text-[9px] text-white/40">The Mark</div>
            <p className="mb-6 text-sm leading-relaxed text-white/50">
              A Mark is a milestone, not a token. Nothing moves on the Ledger — it is
              the World noticing that you did something.
            </p>
            <button
              type="button"
              data-testid="strike-mark-button"
              disabled={busy}
              onClick={strikeMark}
              className="btn btn-gold flex items-center gap-2"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Stamp size={13} />}
              Strike the Mark
            </button>
            <div className="mt-6 space-y-2" data-testid="mark-list">
              <AnimatePresence>
                {marks.map((m) => (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="mono flex items-start gap-2 text-[10px] leading-relaxed text-white/40"
                    data-testid="mark-item"
                  >
                    <Check size={11} className="mt-0.5 shrink-0 text-emerald-400/70" />
                    <span>{m.label}</span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.32 }}
            className="glass rounded-2xl p-7"
            data-testid="export-panel"
          >
            <div className="tracked mb-3 text-[9px] text-white/40">Take it with you</div>
            <p className="mb-6 text-sm leading-relaxed text-white/50">
              Everything — PlayCard, clusters, Mission, Marks and the Ledger lines —
              in plain Markdown or JSON. Nothing is held hostage.
            </p>
            {!exp ? (
              <button
                type="button"
                data-testid="generate-export-button"
                onClick={doExport}
                className="btn flex items-center gap-2"
              >
                <Download size={13} /> Generate export
              </button>
            ) : (
              <>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    data-testid="download-markdown-button"
                    onClick={() => download(exp.markdown, "endeavorum-mission.md", "text/markdown")}
                    className="btn btn-gold flex items-center gap-2"
                  >
                    <Download size={13} /> Markdown
                  </button>
                  <button
                    type="button"
                    data-testid="download-json-button"
                    onClick={() =>
                      download(
                        JSON.stringify(exp.json, null, 2),
                        "endeavorum-mission.json",
                        "application/json"
                      )
                    }
                    className="btn flex items-center gap-2"
                  >
                    <Download size={13} /> JSON
                  </button>
                  <button
                    type="button"
                    data-testid="copy-markdown-button"
                    onClick={() => {
                      navigator.clipboard?.writeText(exp.markdown);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1800);
                    }}
                    className="btn flex items-center gap-2"
                  >
                    {copied ? <Check size={13} /> : <Copy size={13} />} Copy
                  </button>
                </div>
                <pre
                  className="mono mt-6 max-h-56 overflow-auto whitespace-pre-wrap rounded-lg border border-white/8 bg-black/45 p-4 text-[10px] leading-relaxed text-white/45"
                  data-testid="export-preview"
                >
                  {exp.markdown}
                </pre>
              </>
            )}
          </motion.div>
        </div>

        <div className="mt-10 flex flex-wrap gap-5">
          <button type="button" onClick={() => nav("/ledger")} data-testid="mission-ledger-link" className="btn">
            The Ledger
          </button>
          <button type="button" onClick={() => nav("/")} data-testid="mission-atlas-link" className="btn">
            Back to the Atlas
          </button>
        </div>
      </div>
    </div>
  );
}
